// @flow

import { all, put, takeEvery, call } from "redux-saga/effects";
import config from "@config";
import type { Action } from "redux-action";
import http from "@http";
import actionCreators from "@modules/employee/actionCreator";
import {
  GetEmployeeRoleSummary,
  GetEmployeePositionSchema,
  AddEmployeePosition,
  DeleteEmployeePosition,
  UpdateEmployeePosition,
  GetPositionLinkedAbsences,
} from "@modules/employee/constants/actions";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { parseSchema } from "@utils/schema";
import { toast } from "react-toastify";

type EmployeeRoleRequest = $Exact<{
  clientId: string,
  employeeId: string,
  page?: string,
}>;

type EmployeeRoleRequestAddUpdate = $Exact<{
  clientId: string,
  employeeId: string,
  formData: Object,
  positionId?: number | string,
}>;

export function* onGetEmployeeRoleSummary({
  payload,
}: Action<EmployeeRoleRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.employeeId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const page = payload.page || 1;
    const params = {
      page,
      limit: config.resultsPerPage,
    };

    const response = yield call(
      http.get,
      `/api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/employee/${payload.employeeId}/employee-position`,
      {
        params,
      },
    );

    yield put(actionCreators.GetEmployeeRoleSummarySuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetEmployeeRoleSummaryError(e.response));
  }
}

type EmployeePositionSchemaRequest = $Exact<{
  clientId: number,
}>;

export function* onGetEmployeePositionSchema({
  payload,
}: Action<EmployeePositionSchemaRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const schema = yield parseSchema("EmployeePositionDTO", { clientId: payload.clientId });
    yield put(actionCreators.GetEmployeePositionSchemaSuccess(schema));
  } catch (e) {
    yield put(actionCreators.GetEmployeePositionSchemaError(e.response));
  }
}

export function* onDeleteEmployeePosition({
  payload,
}: Action<{
  clientId: number,
  employeeId: number,
  positionId: number,
}>): Generator<*, void, void> {
  const { clientId, employeeId, positionId } = payload || {};
  try {
    yield call(
      http.delete,
      `/api/v1/hierarchy-admin/client/${clientId}/hierarchy/employee/${employeeId}/employee-position/${positionId}`,
    );
    yield all([
      put(actionCreators.DeleteEmployeePositionSuccess()),
      put(actionCreators.GetEmployeeRoleSummary(payload)),
      put(sendSuccessMessage("EMPLOYEE_ROLE_DELETED")),
      put(
        actionCreators.GetEmployees({
          clientId,
          filters: {
            id: employeeId,
          },
        }),
      ),
    ]);
  } catch (error) {
    yield put(actionCreators.DeleteEmployeePositionError());
    toast.error(error.response.data.messages[0]);
  }
}

export function* onAddEmployeePosition({
  payload,
}: Action<EmployeeRoleRequestAddUpdate>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.employeeId || !payload.formData) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    yield call(
      http.post,
      `/api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/employee/${payload.employeeId}/employee-position`,
      payload.formData,
    );

    yield all([
      put(actionCreators.ToggleAddEmployeePositionModal(false)),
      put(actionCreators.GetEmployeeRoleSummary(payload)),
      put(sendSuccessMessage("EMPLOYEE_ROLE_ADDED")),
      put(
        actionCreators.GetEmployees({
          clientId: payload.clientId,
          filters: {
            id: payload.employeeId,
          },
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.GetAddAllergyError(e.response));
  }
}

export function* onUpdateEmployeePosition({
  payload,
}: Action<EmployeeRoleRequestAddUpdate>): Generator<*, void, void> {
  try {
    if (
      !payload ||
      !payload.clientId ||
      !payload.employeeId ||
      !payload.formData ||
      !payload.positionId
    ) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    yield call(
      http.put,
      `/api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/employee/${payload.employeeId}/employee-position/${payload.positionId}`,
      payload.formData,
    );

    yield all([
      put(actionCreators.toggleEditEmployeePositionModal(false)),
      put(actionCreators.UpdateEmployeePositionSuccess()),
      put(
        actionCreators.GetEmployeeRoleSummary({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
      put(sendSuccessMessage("EMPLOYEE_ROLE_UPDATED")),
      put(
        actionCreators.GetEmployees({
          clientId: payload.clientId,
          filters: {
            id: payload.employeeId,
          },
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateEmployeePositionError(e.response));
  }
}

export function* onGetPositionLinkedAbsences({
  payload,
}: Action<{
  clientId: number | string,
  employeeId: number | string,
  positionId: number | string,
}>): Generator<*, void, *> {
  const { clientId, employeeId, positionId } = payload || {};
  try {
    if (!clientId || !employeeId || !positionId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${clientId}/employee/${employeeId}/employee-position/${positionId}/absence`,
    );

    const data = response.data || {};

    yield put(actionCreators.GetPositionLinkedAbsencesSuccess(data.content || data));
  } catch (e) {
    yield put(actionCreators.GetPositionLinkedAbsencesError(e.response));
  }
}

export function* EmploymentDetailsSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetEmployeeRoleSummary, onGetEmployeeRoleSummary),
    takeEvery(GetEmployeePositionSchema, onGetEmployeePositionSchema),
    takeEvery(AddEmployeePosition, onAddEmployeePosition),
    takeEvery(DeleteEmployeePosition, onDeleteEmployeePosition),
    takeEvery(UpdateEmployeePosition, onUpdateEmployeePosition),
    takeEvery(GetPositionLinkedAbsences, onGetPositionLinkedAbsences),
  ]);
}

export default EmploymentDetailsSagas;
